import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import { journeyDefinitions } from "@seeing-maths/content-runtime";
import type {
  AppendResult,
  JourneyId,
  LearnerProjectionV1,
  LearningEventV1,
  LocalIdentity,
  PersistencePort,
  ResumeCheckpoint,
} from "@seeing-maths/contracts";
import { createLearnerProjection, projectEvent } from "@seeing-maths/learning-core";

interface SeeingMathsDb extends DBSchema {
  meta: {
    key: string;
    value: LocalIdentity;
  };
  projections: {
    key: string;
    value: LearnerProjectionV1;
  };
  events: {
    key: string;
    value: LearningEventV1;
    indexes: { "by-learner-sequence": [string, number] };
  };
  outbox: {
    key: string;
    value: LearningEventV1;
    indexes: { "by-learner-sequence": [string, number] };
  };
}

const DB_NAME = "seeing-maths-local-v1";
const IDENTITY_KEY = "local-identity-v1";

export class IndexedDbPersistence implements PersistencePort {
  private databasePromise: Promise<IDBPDatabase<SeeingMathsDb>> | null = null;

  constructor(private readonly databaseName = DB_NAME) {}

  private database() {
    this.databasePromise ??= openDB<SeeingMathsDb>(this.databaseName, 1, {
      upgrade(database) {
        database.createObjectStore("meta");
        database.createObjectStore("projections", { keyPath: "learnerId" });
        const events = database.createObjectStore("events", { keyPath: "eventId" });
        events.createIndex("by-learner-sequence", ["learnerId", "clientSequence"]);
        const outbox = database.createObjectStore("outbox", { keyPath: "eventId" });
        outbox.createIndex("by-learner-sequence", ["learnerId", "clientSequence"]);
      },
    });
    return this.databasePromise;
  }

  async getOrCreateIdentity(): Promise<LocalIdentity> {
    const database = await this.database();
    const existing = await database.get("meta", IDENTITY_KEY);
    if (existing) return existing;
    const identity: LocalIdentity = {
      learnerId: `guest-${crypto.randomUUID()}`,
      deviceId: `device-${crypto.randomUUID()}`,
    };
    await database.put("meta", identity, IDENTITY_KEY);
    return identity;
  }

  async loadProjection(learnerId: string): Promise<LearnerProjectionV1> {
    const database = await this.database();
    return (await database.get("projections", learnerId)) ?? createLearnerProjection(learnerId);
  }

  async appendEvents(events: LearningEventV1[]): Promise<AppendResult> {
    if (events.length === 0) {
      const learnerId = (await this.getOrCreateIdentity()).learnerId;
      return { projection: await this.loadProjection(learnerId), acceptedEventIds: [] };
    }

    const learnerId = events[0].learnerId;
    if (events.some((event) => event.learnerId !== learnerId)) {
      throw new Error("A persistence transaction can contain only one learner.");
    }

    const database = await this.database();
    const transaction = database.transaction(
      ["events", "outbox", "projections"],
      "readwrite",
    );
    let projection =
      (await transaction.objectStore("projections").get(learnerId)) ??
      createLearnerProjection(learnerId);
    const acceptedEventIds: string[] = [];

    for (const event of events) {
      const exists = await transaction.objectStore("events").getKey(event.eventId);
      if (exists) continue;
      await transaction.objectStore("events").put(event);
      await transaction.objectStore("outbox").put(event);
      projection = projectEvent(projection, event, journeyDefinitions[event.conceptId]);
      acceptedEventIds.push(event.eventId);
    }

    await transaction.objectStore("projections").put(projection);
    await transaction.done;
    return { projection, acceptedEventIds };
  }

  async saveCheckpoint(
    learnerId: string,
    conceptId: JourneyId,
    checkpoint: ResumeCheckpoint,
  ): Promise<LearnerProjectionV1> {
    const database = await this.database();
    const transaction = database.transaction("projections", "readwrite");
    const projection =
      (await transaction.store.get(learnerId)) ?? createLearnerProjection(learnerId);
    const concept = projection.concepts[conceptId] ?? {
      visibleState: "discovered" as const,
      stage: checkpoint.stage,
      evidence: {
        intuition: 0,
        manipulation: 0,
        symbolic: 0,
        explanation: 0,
        transfer: 0,
        retention: 0,
      },
      representationsSucceeded: [],
      recentIndependentCorrect: 0,
      misconceptions: [],
      resumeCheckpoint: null,
    };
    const next: LearnerProjectionV1 = {
      ...projection,
      concepts: {
        ...projection.concepts,
        [conceptId]: {
          ...concept,
          stage: checkpoint.stage,
          resumeCheckpoint: checkpoint,
        },
      },
    };
    await transaction.store.put(next);
    await transaction.done;
    return next;
  }

  async listEvents(learnerId: string, limit = 40): Promise<LearningEventV1[]> {
    const database = await this.database();
    const events = await database.getAllFromIndex(
      "events",
      "by-learner-sequence",
      IDBKeyRange.bound([learnerId, 0], [learnerId, Number.MAX_SAFE_INTEGER]),
    );
    return events.slice(-Math.max(0, limit));
  }

  async acknowledgeSync(eventIds: string[]): Promise<void> {
    const database = await this.database();
    const transaction = database.transaction("outbox", "readwrite");
    await Promise.all(eventIds.map((eventId) => transaction.store.delete(eventId)));
    await transaction.done;
  }

  async resetLearner(learnerId: string): Promise<void> {
    const database = await this.database();
    const transaction = database.transaction(
      ["events", "outbox", "projections"],
      "readwrite",
    );
    await transaction.objectStore("projections").delete(learnerId);
    for (const storeName of ["events", "outbox"] as const) {
      const index = transaction.objectStore(storeName).index("by-learner-sequence");
      let cursor = await index.openCursor(
        IDBKeyRange.bound([learnerId, 0], [learnerId, Number.MAX_SAFE_INTEGER]),
      );
      while (cursor) {
        await cursor.delete();
        cursor = await cursor.continue();
      }
    }
    await transaction.done;
  }
}
